import Component, { loadComponent } from '../../../assets/scripts/modules/component.js'
import debounce from '../../../assets/scripts/utilities/debounce.js'
import { prefersReducedMotion, supportsHover } from '../../../assets/scripts/utilities/media-queries.js'

const HIDDEN_BUTTON_CLASS = 'is-hidden'
const OVERFLOWING_CLASS = 'is-overflowing'
const AUTOPLAYING_CLASS = 'is-autoplaying'
const CLONE_ATTR = 'data-clone'
const INTERACTION_PAUSE_MS = 2500

class LogoCarouselComponent extends Component {
  init() {
    this.trackWrap = this.element.querySelector('[data-track-wrap]')
    this.track = this.element.querySelector('[data-track]')
    this.entries = [...this.element.querySelectorAll('[data-entry]')]
    this.prevButton = this.element.querySelector('[data-nav-button="prev"]')
    this.nextButton = this.element.querySelector('[data-nav-button="next"]')

    if (!this.trackWrap || !this.track || !this.entries.length || !this.prevButton || !this.nextButton) {
      return
    }

    this.animationFrameId = null
    this.resizeObserver = null
    this.intersectionObserver = null
    this.reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.scrollStep = 0
    this.autoplaySpeed = 36
    this.lastFrameTimestamp = 0
    // Fractional autoplay position. scrollLeft is rounded to device pixels by
    // the browser, so sub-pixel per-frame movement must be accumulated here.
    this.autoplayPosition = 0
    this.isAutoplayActive = false
    this.isHoverPaused = false
    this.isInteractionPaused = false
    this.isOffscreenPaused = false
    this.interactionTimeout = null
    // loopWidth is measured from the DOM after clones are inserted so it is
    // always exact, regardless of gaps or sub-pixel rounding.
    this.loopWidth = 0
    this.loopClones = []
    this.originalEntries = []

    this.handlePrevButtonClick = this.handlePrevButtonClick.bind(this)
    this.handleNextButtonClick = this.handleNextButtonClick.bind(this)
    this.handlePointerEnter = this.handlePointerEnter.bind(this)
    this.handlePointerLeave = this.handlePointerLeave.bind(this)
    this.handlePointerDown = this.handlePointerDown.bind(this)
    this.handlePointerUp = this.handlePointerUp.bind(this)
    this.handleWheel = this.handleWheel.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this)
    this.handleAutoplayFrame = this.handleAutoplayFrame.bind(this)
    this.handleIntersectionChange = this.handleIntersectionChange.bind(this)
    this.handleResize = debounce(() => this.updateCarouselState(), 150)

    this.bindEvents()
    this.startObservers()
    this.updateCarouselState()
  }

  reinit() {
    this.destroy()
    this.init()
  }

  destroy() {
    this.stopAutoplay()
    clearTimeout(this.interactionTimeout)
    this.removeLoopClones()

    this.prevButton?.removeEventListener('click', this.handlePrevButtonClick)
    this.nextButton?.removeEventListener('click', this.handleNextButtonClick)
    this.trackWrap?.removeEventListener('mouseenter', this.handlePointerEnter)
    this.trackWrap?.removeEventListener('mouseleave', this.handlePointerLeave)
    this.trackWrap?.removeEventListener('pointerdown', this.handlePointerDown)
    this.trackWrap?.removeEventListener('pointerup', this.handlePointerUp)
    this.trackWrap?.removeEventListener('wheel', this.handleWheel)
    this.trackWrap?.removeEventListener('keydown', this.handleKeydown)
    this.reducedMotionMediaQuery?.removeEventListener('change', this.handleReducedMotionChange)
    window.removeEventListener('resize', this.handleResize)

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }

  bindEvents() {
    this.prevButton.addEventListener('click', this.handlePrevButtonClick)
    this.nextButton.addEventListener('click', this.handleNextButtonClick)

    if (supportsHover()) {
      this.trackWrap.addEventListener('mouseenter', this.handlePointerEnter)
      this.trackWrap.addEventListener('mouseleave', this.handlePointerLeave)
    }

    this.trackWrap.addEventListener('pointerdown', this.handlePointerDown)
    this.trackWrap.addEventListener('pointerup', this.handlePointerUp)
    this.trackWrap.addEventListener('wheel', this.handleWheel, { passive: true })
    this.trackWrap.addEventListener('keydown', this.handleKeydown)
    this.reducedMotionMediaQuery.addEventListener('change', this.handleReducedMotionChange)
  }

  startObservers() {
    if (window.IntersectionObserver) {
      // Pause autoplay while the carousel is scrolled out of view so it does
      // not burn a rAF loop (and scrollLeft writes) for an invisible element.
      // The observer fires once on observe(), establishing the initial state.
      this.intersectionObserver = new IntersectionObserver(this.handleIntersectionChange, {
        rootMargin: '100px 0px',
      })
      this.intersectionObserver.observe(this.trackWrap)
    }

    if (!window.ResizeObserver) {
      // Fallback for environments without ResizeObserver support.
      window.addEventListener('resize', this.handleResize)
      return
    }

    this.resizeObserver = new ResizeObserver(() => this.handleResize())

    // Observe the visible container for viewport-driven changes, and each
    // entry for content-driven changes (e.g. logo images loading in after
    // init, which changes the track's scroll width without resizing the
    // component element itself). ResizeObserver also fires once on observe(),
    // which establishes the correct initial state.
    this.resizeObserver.observe(this.trackWrap)
    this.entries.forEach(entry => this.resizeObserver.observe(entry))
  }

  updateMetrics() {
    this.originalEntries = [...this.track.querySelectorAll(`[data-entry]:not([${CLONE_ATTR}])`)]

    if (!this.originalEntries.length) {
      this.scrollStep = 0
      return
    }

    const trackStyles = window.getComputedStyle(this.track)
    const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0
    const entryWidth = this.originalEntries[0].getBoundingClientRect().width

    this.scrollStep = entryWidth + gap

    // Keep loopWidth in sync when entries resize after the clones were created
    // (e.g. images finishing loading), otherwise it goes stale.
    this.measureLoopWidth()
  }

  measureLoopWidth() {
    if (!this.loopClones.length || !this.originalEntries.length) {
      return
    }

    // Measure the actual distance between the first original entry and the
    // first clone. getBoundingClientRect differences are scroll-offset-neutral,
    // so this is accurate no matter what the current scrollLeft is.
    const firstOriginalRect = this.originalEntries[0].getBoundingClientRect()
    const firstCloneRect = this.loopClones[0].getBoundingClientRect()

    this.loopWidth = firstCloneRect.left - firstOriginalRect.left
  }

  hasOverflow() {
    // When clones are present loopWidth reflects the original sequence length, so use that.
    // Otherwise read scrollWidth directly (no clones yet).
    let isOverflowing = false

    if (this.loopWidth) {
      isOverflowing = this.loopWidth > this.track.clientWidth + 1
    } else {
      isOverflowing = this.track.scrollWidth - this.track.clientWidth > 1
    }

    return isOverflowing
  }

  ensureLoopClones() {
    if (!this.originalEntries.length || this.loopClones.length) {
      return
    }

    const fragment = document.createDocumentFragment()

    const clones = this.originalEntries.map(entry => {
      const clone = entry.cloneNode(true)

      clone.setAttribute(CLONE_ATTR, 'true')
      clone.setAttribute('aria-hidden', 'true')
      clone.removeAttribute('id')

      clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'))
      clone.querySelector('a')?.setAttribute('tabindex', '-1')

      fragment.appendChild(clone)
      return clone
    })

    this.track.appendChild(fragment)
    this.loopClones = clones

    this.measureLoopWidth()
  }

  removeLoopClones() {
    this.loopClones.forEach(clone => clone.remove())
    this.loopClones = []
    this.loopWidth = 0
  }

  normalizeLoopPosition() {
    if (!this.loopWidth) {
      return
    }

    if (this.track.scrollLeft >= this.loopWidth) {
      this.track.scrollLeft = this.track.scrollLeft % this.loopWidth
    }
  }

  updateCarouselState() {
    this.updateMetrics()

    const overflowing = this.hasOverflow()

    this.element.classList.toggle(OVERFLOWING_CLASS, overflowing)
    this.toggleButtonsVisibility(overflowing)

    if (!overflowing) {
      this.isInteractionPaused = false
      this.isHoverPaused = false
      clearTimeout(this.interactionTimeout)
      this.stopAutoplay()
      this.removeLoopClones()
      this.track.scrollLeft = 0
      return
    }

    this.ensureLoopClones()
    this.normalizeLoopPosition()

    if (prefersReducedMotion()) {
      this.stopAutoplay()
      return
    }

    this.maybeResumeAutoplay()
  }

  toggleButtonsVisibility(areVisible) {
    const buttons = [this.prevButton, this.nextButton]

    buttons.forEach(button => {
      if (!button) {
        return
      }

      button.classList.toggle(HIDDEN_BUTTON_CLASS, !areVisible)
      button.disabled = !areVisible
      button.setAttribute('aria-hidden', String(!areVisible))
      button.tabIndex = areVisible ? 0 : -1
    })
  }

  startAutoplay() {
    if (this.isAutoplayActive || !this.hasOverflow() || prefersReducedMotion()) {
      return
    }
    this.ensureLoopClones()
    this.normalizeLoopPosition()
    this.isAutoplayActive = true
    this.autoplayPosition = this.track.scrollLeft
    this.lastFrameTimestamp = 0
    this.element.classList.add(AUTOPLAYING_CLASS)
    this.animationFrameId = window.requestAnimationFrame(this.handleAutoplayFrame)
  }

  stopAutoplay() {
    this.isAutoplayActive = false
    this.element.classList.remove(AUTOPLAYING_CLASS)

    if (this.animationFrameId) {
      window.cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  maybeResumeAutoplay() {
    if (this.isHoverPaused || this.isInteractionPaused || this.isOffscreenPaused) {
      return
    }

    this.startAutoplay()
  }

  handleIntersectionChange(entries) {
    const isVisible = entries.some(entry => entry.isIntersecting)

    this.isOffscreenPaused = !isVisible

    if (this.isOffscreenPaused) {
      this.stopAutoplay()
      return
    }

    this.maybeResumeAutoplay()
  }

  handleAutoplayFrame(timestamp) {
    if (!this.isAutoplayActive) {
      return
    }

    if (this.loopWidth <= 0) {
      this.stopAutoplay()
      return
    }

    // On the first frame lastFrameTimestamp is 0; rAF timestamps are vsync
    // times that can predate a performance.now() call, so treat it as elapsed 0
    // and clamp to [0, 0.05] to survive tab switches and slow frames.
    const elapsed = this.lastFrameTimestamp ? Math.min(Math.max((timestamp - this.lastFrameTimestamp) / 1000, 0), 0.05) : 0

    this.lastFrameTimestamp = timestamp
    this.autoplayPosition += this.autoplaySpeed * elapsed

    // Wrap using subtraction rather than modulo to avoid a large discontinuity
    // if multiple loop widths are crossed in a single (slow) frame.
    while (this.autoplayPosition >= this.loopWidth) {
      this.autoplayPosition -= this.loopWidth
    }

    this.track.scrollLeft = this.autoplayPosition
    this.animationFrameId = window.requestAnimationFrame(this.handleAutoplayFrame)
  }

  pauseForInteraction() {
    this.isInteractionPaused = true
    this.stopAutoplay()
    clearTimeout(this.interactionTimeout)

    this.interactionTimeout = setTimeout(() => {
      this.isInteractionPaused = false
      this.maybeResumeAutoplay()
    }, INTERACTION_PAUSE_MS)
  }

  scrollByEntry(direction) {
    if (!this.scrollStep) {
      return
    }

    this.normalizeLoopPosition()

    const currentIndex = Math.round(this.track.scrollLeft / this.scrollStep)
    const targetIndex = Math.max(0, currentIndex + direction)
    const targetScrollLeft = targetIndex * this.scrollStep

    this.track.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    })
  }

  handlePrevButtonClick() {
    this.pauseForInteraction()
    this.scrollByEntry(-1)
  }

  handleNextButtonClick() {
    this.pauseForInteraction()
    this.scrollByEntry(1)
  }

  handlePointerEnter() {
    this.isHoverPaused = true
    this.stopAutoplay()
  }

  handlePointerLeave() {
    this.isHoverPaused = false
    this.maybeResumeAutoplay()
  }

  handlePointerDown() {
    this.pauseForInteraction()
  }

  handlePointerUp() {
    this.isInteractionPaused = false
    this.maybeResumeAutoplay()
  }

  handleWheel() {
    this.pauseForInteraction()
  }

  handleKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return
    }
    event.preventDefault()
    this.pauseForInteraction()

    if (event.key === 'ArrowLeft') {
      this.scrollByEntry(-1)
      return
    }

    this.scrollByEntry(1)
  }

  handleReducedMotionChange() {
    this.updateCarouselState()
  }
}

window.addEventListener('init-load', () => loadComponent(document, 'logo-carousel', LogoCarouselComponent))
